<template>
  <div :class="sidebarClasses" class="d-flex flex-column" style="width: 240px;">
    <div class="sidebar-logo text-center mb-4">
      <img src="/logo_b.png" alt="SICECOM" class="client-sidebar-logo" />
    </div>
    
    <div v-if="loadingModules" class="text-center py-4 flex-grow-1">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>
    
    <ul v-else class="nav flex-column flex-nowrap flex-grow-1 scrollable-menu w-100">
      <li v-for="(section, idx) in menuSections" :key="section.code" class="nav-item mb-2 w-100">
        <template v-if="section.to">
          <router-link :to="section.to" class="nav-link d-flex align-items-center w-100">
            <span class="d-flex align-items-center text-truncate me-2">
              <i :class="getModuleIcon(section.code)" class="me-2"></i>
              {{ section.label }}
            </span>
          </router-link>
        </template>
        <template v-else>
          <div 
            class="nav-link d-flex justify-content-between align-items-center w-100" 
            style="cursor:pointer" 
            @click="toggleMenu(idx)" 
            :aria-expanded="isOpen(idx)" 
            tabindex="0" 
            @keydown.enter.prevent="toggleMenu(idx)"
          >
            <span class="d-flex align-items-center text-truncate me-2">
              <i :class="getModuleIcon(section.code)" class="me-2"></i>
              {{ section.label }}
            </span>
            <span v-if="section.children && section.children.length > 0" class="toggle-icon flex-shrink-0">
              <i :class="isOpen(idx) ? 'bi bi-dash-circle' : 'bi bi-plus-circle'"></i>
            </span>
          </div>
        </template>
        <ul v-show="isOpen(idx)" class="nav flex-column flex-nowrap ms-3 mt-1 w-100">
            <li v-for="item in section.children" :key="item.code" class="nav-item mb-1 w-100">
              <!-- Sub-grupo colapsable (nivel 3) -->
              <template v-if="item.children && item.children.length > 0">
                <div
                  class="nav-link submenu-link submenu-group-header d-flex justify-content-between align-items-center w-100"
                  style="cursor:pointer"
                  @click="toggleSubmenu(item.code)"
                >
                  <span class="d-flex align-items-center text-truncate me-1">
                    <i class="bi bi-folder2 me-1"></i>{{ item.label }}
                  </span>
                  <i :class="isSubmenuOpen(item.code) ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="flex-shrink-0"></i>
                </div>
                <ul v-show="isSubmenuOpen(item.code)" class="nav flex-column flex-nowrap ms-3 mt-1 w-100">
                    <li v-for="subitem in item.children" :key="subitem.code" class="nav-item mb-1 w-100">
                      <router-link
                        v-if="subitem.to"
                        :to="subitem.to"
                        class="nav-link submenu-link submenu-level-3 d-block text-truncate"
                      >
                        <i class="bi bi-dash me-1"></i>{{ subitem.label }}
                      </router-link>
                    </li>
                  </ul>
              </template>
              <!-- Item directo (nivel 2) -->
              <template v-else>
                <router-link v-if="item.to" :to="item.to" class="nav-link submenu-link d-block text-truncate">
                  {{ item.label }}
                </router-link>
                <span v-else class="nav-link submenu-link d-block text-truncate">{{ item.label }}</span>
              </template>
            </li>
          </ul>
      </li>
    </ul>

    <div class="mt-auto pt-3 border-top">
      <ul class="nav flex-column">
        <li class="nav-item">
          <a href="#" @click.prevent="handleLogout" class="nav-link text-danger">
            <i class="bi bi-box-arrow-right me-2"></i>Cerrar sesión
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { cerrarSesion } from '@/services/servicioAutenticacion'
import themeManager from '@/store/theme'

export default {
  name: 'ClientSidebar',
  data() {
    return {
      openMenus: [],
      activeModules: [],
      loadingModules: true,
      openSubmenus: []
    }
  },
  mounted() {
    this.loadActiveModules()
  },
  computed: {
    sidebarClasses() {
      return `vh-100 p-3 ${themeManager.themeClass}`;
    },
    menuSections() {
      if (this.loadingModules) return []

      // Si no hay módulos activos (por ejemplo en modo desarrollo o cuando omitimos validaciones),
      // devolver un menú por defecto para que la navegación sea usable.
      if (!this.activeModules || this.activeModules.length === 0) {
        return this.getDefaultMenuSections()
      }

      // Construir el menú jerárquico desde los módulos activos
      return this.buildMenuHierarchy(this.activeModules)
    }
  },
  
  methods: {
    getDefaultMenuSections() {
      return [
        { code: '1', label: 'Inicio', to: '/dashboard', children: [] },
        {
          code: '3',
          label: 'Facturación',
          children: [
            { code: '3.1', label: 'Nueva factura', to: '/ventas/facturacion' },
            { code: '3.2', label: 'Documentos emitidos', to: '/ventas/documentos-emitidos' },
            { code: '3.3', label: 'Notas de crédito', to: '/ventas/notas-credito' },
            { code: '3.4', label: 'Guías de remisión', to: '/ventas/guias-remision' }
          ]
        },
        {
          code: 'PF',
          label: 'Proformas',
          children: [
            { code: 'PF.1', label: 'Listado', to: '/proformas' },
            { code: 'PF.2', label: 'Nueva proforma', to: '/proformas/nueva' }
          ]
        },
        {
          code: '2',
          label: 'Clientes',
          children: [
            { code: '2.1', label: 'Clientes', to: '/personas/clientes' },
            { code: '2.2', label: 'Estado de cuenta', to: '/clientes/estado-cuenta' },
            { code: '2.3', label: 'Carga masiva', to: '/personas/clientes/carga-masiva' }
          ]
        },
        {
          code: '5',
          label: 'Productos',
          children: [
            { code: '5.1', label: 'Productos', to: '/inventario/productos' },
            { code: '5.2', label: 'Categorías', to: '/inventario/categorias' },
            { code: '5.3', label: 'Marcas', to: '/inventario/marcas' },
            { code: '5.4', label: 'Unidades', to: '/inventario/unidades-medida' },
            { code: '5.5', label: 'Almacenes', to: '/inventario/almacenes' },
            { code: '5.6', label: 'Stock', to: '/inventario/stock' },
            { code: '5.7', label: 'Carga masiva', to: '/inventario/carga-masiva' }
          ]
        },
        {
          code: '4',
          label: 'Compras',
          children: [
            { code: '4.0', label: 'Mis compras', to: '/compras/ordenes' },
            { code: '4.1', label: 'Registrar compra', to: '/compras/registrar' },
            { code: '4.2', label: 'Proveedores', to: '/personas/proveedores' },
            { code: '4.3', label: 'Carga masiva prov.', to: '/personas/proveedores/carga-masiva' },
            { code: '4.5', label: 'Estado de cuenta', to: '/compras/estado-cuenta' }
          ]
        },
        {
          code: '6',
          label: 'Caja',
          children: [
            { code: '6.1', label: 'Control de Caja',         to: '/cajas' },
            { code: '6.2', label: 'Historial sesiones',      to: '/cajas/historial' },
            { code: '6.3', label: 'Configurar cajas',        to: '/cajas/configuracion' },
            { code: '6.4', label: 'Bancos y movimientos',    to: '/finanzas/bancos' },
          ]
        },
        {
          code: '8',
          label: 'Reportes',
          children: [
            { code: '8.1', label: 'Ventas', to: '/reportes/ventas' },
            { code: '8.2', label: 'Utilidad aproximada', to: '/reportes/utilidad-aproximada' },
            { code: '8.3', label: 'Productos vendidos', to: '/reportes/productos-vendidos' },
            { code: '8.4', label: 'Clientes con deuda', to: '/reportes/clientes-deuda' },
            { code: '8.5', label: 'Proveedores con deuda', to: '/reportes/proveedores-deuda' },
            { code: '8.6', label: 'ATS', to: '/reportes/ats' }
          ]
        },
        {
          code: '9',
          label: 'Configuración',
          children: [
            { code: '9.1', label: 'SRI / Facturación', to: '/configuracion/sri' },
            { code: '9.2', label: 'Secuenciales', to: '/configuracion/secuenciales' },
            { code: '9.3', label: 'Usuarios', to: '/configuracion/usuarios' },
            { code: '9.4', label: 'Correos', to: '/configuracion/correos' },
            { code: '9.5', label: 'Plantillas', to: '/configuracion/plantillas' },
            { code: '9.6', label: 'Impresiones', to: '/configuracion/impresiones' }
          ]
        }
      ]
    },

    async loadActiveModules() {
      // Forzar menú por defecto: ignorar API y mostrar todos los módulos
      this.activeModules = []
      this.loadingModules = false
    },

    buildMenuHierarchy(modules) {
      // Filtrar solo módulos activos
      const activeModules = modules.filter(module => module.is_active == 1)
      
      // Agrupar módulos por nivel principal (sin punto decimal)
      const hierarchy = []
      const moduleMap = new Map()

      // Crear mapa de módulos para acceso rápido por código
      activeModules.forEach(module => {
        moduleMap.set(module.code, module)
      })

      // Identificar módulos padres (is_core = 1)
      activeModules.forEach(module => {
        if (module.is_core === 1) {
          // Es un módulo principal
          const children = activeModules
            .filter(m => m.code.startsWith(module.code + '.') && m.code.split('.').length === 2)
            .map(m => ({
              code: m.code,
              label: m.name,
              to: this.getModuleRoute(m.code)
            }))
            .sort((a, b) => {
              // Ordenar por la parte decimal del código
              const aParts = a.code.split('.')
              const bParts = b.code.split('.')
              const aNum = parseInt(aParts[1] || 0)
              const bNum = parseInt(bParts[1] || 0)
              return aNum - bNum
            })

          hierarchy.push({
            code: module.code,
            label: module.name,
            children: children
          })
        }
      })

      // Ordenar módulos padres numéricamente
      hierarchy.sort((a, b) => {
        const numA = parseInt(a.code)
        const numB = parseInt(b.code)
        return numA - numB
      })

      return hierarchy
    },

    getModuleRoute(code) {
      // Mapeo de rutas según el código del módulo
      const routeMap = {
        // Inicio
        '1.1': '/dashboard',
        
        // Personas
        '2.1': '/personas/clientes',
        '2.2': '/personas/proveedores',
        '2.3': '/personas/empleados',
        '2.4': '/personas/usuarios',
        '2.5': '/personas/roles-permisos',
        '2.6': '/personas/direcciones',
        '2.7': '/personas/historial',
        
        // Ventas
        '3.1': '/ventas/cotizaciones',
        '3.2': '/ventas/pedidos',
        '3.3': '/ventas/facturacion',
        '3.9': '/ventas/documentos-emitidos',
        '3.4': '/ventas/notas-credito',
        '3.5': '/ventas/entregas',
        '3.6': '/ventas/cobros',
        '3.7': '/ventas/pos',
        '3.8': '/ventas/historial',
        
        // Compras
        '4.0': '/compras/ordenes',
        '4.1': '/compras/solicitudes',
        '4.2': '/compras/ordenes',
        '4.3': '/compras/recepcion',
        '4.4': '/compras/pagos',
        '4.5': '/compras/historial',
        
        // Inventario
        '5.1': '/inventario/productos',
        '5.2': '/inventario/categorias',
        '5.3': '/inventario/almacenes',
        '5.4': '/inventario/movimientos',
        '5.5': '/inventario/transferencias',
        '5.6': '/inventario/ajustes',
        '5.7': '/inventario/stock',
        
        // Finanzas
        '6.1': '/finanzas/cuentas-cobrar',
        '6.2': '/finanzas/cuentas-pagar',
        '6.3': '/finanzas/flujo-caja',
        '6.4': '/finanzas/bancos',
        '6.5': '/finanzas/conciliaciones',
        '6.6': '/finanzas/presupuestos',
        
        // Contabilidad
        '7.1': '/contabilidad/plan-cuentas',
        '7.2': '/contabilidad/asientos',
        '7.3': '/contabilidad/mayor-general',
        '7.4': '/contabilidad/balance-general',
        '7.5': '/contabilidad/estado-resultados',
        '7.6': '/contabilidad/cierre',
        
        // Reportes
        '8.1': '/reportes/ventas',
        '8.2': '/reportes/utilidad-aproximada',
        '8.3': '/reportes/productos-vendidos',
        '8.4': '/reportes/clientes-deuda',
        '8.5': '/reportes/proveedores-deuda',
        '8.6': '/reportes/ats',
        
        // Configuración
        '9.1': '/configuracion/sri',
        '9.2': '/configuracion/secuenciales',
        '9.3': '/configuracion/usuarios',
        '9.4': '/configuracion/correos',
        '9.5': '/configuracion/plantillas',
        '9.6': '/configuracion/impresiones',
        '9.7': '/configuracion/backups'
      }

      return routeMap[code] || null
    },

    getModuleIcon(code) {
      // Mapeo de iconos por código de módulo principal
      const iconMap = {
        '1': 'bi bi-house-door',        // Inicio
        '2': 'bi bi-people',            // Personas
        '3': 'bi bi-cart',              // Ventas
        '4': 'bi bi-bag',               // Compras
        '5': 'bi bi-box-seam',          // Inventario
        '6': 'bi bi-cash-stack',         // Caja
        '7': 'bi bi-journal-text',      // Contabilidad
        '8': 'bi bi-bar-chart',         // Reportes
        '9': 'bi bi-gear',              // Configuración
        'PF': 'bi bi-file-earmark-text' // Proformas
      }
      
      return iconMap[code] || 'bi bi-circle'
    },

    toggleMenu(menuIndex) {
      if (this.openMenus.includes(menuIndex)) {
        this.openMenus = this.openMenus.filter(idx => idx !== menuIndex)
      } else {
        this.openMenus = [menuIndex]
      }
    },

    isOpen(menuName) {
      return this.openMenus.includes(menuName)
    },

    toggleSubmenu(codigo) {
      if (this.openSubmenus.includes(codigo)) {
        this.openSubmenus = this.openSubmenus.filter(c => c !== codigo)
      } else {
        this.openSubmenus = [...this.openSubmenus, codigo]
      }
    },

    isSubmenuOpen(codigo) {
      return this.openSubmenus.includes(codigo)
    },

    handleLogout() {
      cerrarSesion()
      this.$router.push('/iniciar-sesion')
    }
  }
}
</script>

<style scoped>


.toggle-icon {
  font-weight: bold;
  font-size: 1.1em;
}

.toggle-icon i {
  color: var(--primary-main) !important;
}

/* Iconos del menú principal */
.nav-link > span > i:first-child {
  font-size: 1.1em;
  width: 20px;
  text-align: center;
  color: var(--primary-main);
}

.submenu-link {
  padding-left: 1rem;
  font-size: 0.9em;
  color: var(--text-secondary) !important;
}

.submenu-link:hover {
  color: var(--primary-main) !important;
}

.nav-link {
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.nav-link:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.scrollable-menu {
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0; /* Crucial para que el scroll funcione dentro de flexbox */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

/* Webkit (Chrome, Safari, Edge) scrollbar styling */
.scrollable-menu::-webkit-scrollbar {
  width: 6px;
}

.scrollable-menu::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-menu::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.scrollable-menu::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Tema claro - texto negro en el sidebar */
.light-theme .nav-link {
  color: #212529 !important;
}

.light-theme .nav-link:hover {
  color: #000 !important;
}

.light-theme .submenu-link {
  color: #495057 !important;
}

.light-theme .submenu-link:hover {
  color: #0d6efd !important;
}

/* Tema oscuro - texto claro en el sidebar */
.dark-theme .nav-link {
  color: #e9ecef !important;
}

.dark-theme .nav-link:hover {
  color: #fff !important;
}

.dark-theme .submenu-link {
  color: #adb5bd !important;
}

.dark-theme .submenu-link:hover {
  color: #0d6efd !important;
}
</style>





